import './firebase';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import useAuthStore from './store/authStore';
import { DarkModeProvider } from './contexts/DarkModeContext';

import Auth from './components/Auth';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import ChatPanel from './components/ChatPanel';
import ProductDetail from './components/ProductDetail';
import { messages as sidebarMessages } from './components/Sidebar';
import Home from './components/Home';
import { db } from './firebase';
import { collection, query, orderBy, onSnapshot, getDocs, doc, updateDoc, getDoc, where } from 'firebase/firestore';
import useSetOnlineStatus from './hooks/useSetOnlineStatus';
import UseSetOnlineStatusGlobal from './hooks/UseSetOnlineStatusGlobal';
import Marketplace from './components/Marketplace';
import BuyYouTubeChannel from './components/BuyYouTubeChannel';
import BuyTiktokAccount from './components/BuyTiktokAccount';
import BuyFacebookPage from './components/BuyFacebookPage';
import BuyInstagramAccount from './components/BuyInstagramAccount';
import ProductView from './components/ProductView';
import Header from './components/Header';
import Footer from './components/Footer';
import SellAccount from './pages/SellAccount';
import AdminDashboardSite from './pages/admindashboardsite';
import ProfilePage from './pages/profile';
import WisePaymentDetails from './pages/WisePaymentDetails';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaExclamationCircle } from 'react-icons/fa';
import NotFound from './components/NotFound';
import Unauthorized from './components/Unauthorized';
import AdminLogin from './pages/AdminLogin';
import ResetPassword from './pages/ResetPassword';
import MobileFooterBar from './components/MobileFooterBar';
import GoogleAnalytics from './components/GoogleAnalytics';
import CacheManager from './components/CacheManager';

// --- Configurable admin email (now from .env) ---
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
if (!ADMIN_EMAIL) {
  // Optionally log or throw an error in dev
  // console.warn('ADMIN_EMAIL is not set! Please check your .env file.');
}

// --- ErrorBoundary component ---
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    // Log error to monitoring service if needed
    // console.error('ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <div style={{padding: 40, textAlign: 'center'}}>Something went wrong. Please refresh the page.</div>;
    }
    return this.props.children;
  }
}

// --- Lazy load heavy components ---
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const Checkout = React.lazy(() => import('./components/Checkout'));
const Blog = React.lazy(() => import('./components/Blog'));
const BlogDetail = React.lazy(() => import('./components/BlogDetail'));
const AboutUs = React.lazy(() => import('./components/AboutUs'));
const ContactUs = React.lazy(() => import('./components/ContactUs'));
const DeliveryFAQ = React.lazy(() => import('./components/DeliveryFAQ'));
const TermsAndConditions = React.lazy(() => import('./components/TermsAndConditions'));
const RefundAndReturnPolicy = React.lazy(() => import('./components/RefundAndReturnPolicy'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const Wishlist = React.lazy(() => import('./components/Wishlist'));
const Cart = React.lazy(() => import('./components/Cart'));

function ChatApp() {
  useSetOnlineStatus();
  const { user, isAdmin } = useAuthStore();
  const [selectedConversation, setSelectedConversation] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('transactions');
  const [transactions, setTransactions] = React.useState([]);
  const [messages, setMessages] = React.useState([]);
  const [agentMessages, setAgentMessages] = React.useState([]);
  const [sidebarLoading, setSidebarLoading] = React.useState(true);
  const [directConversationLoading, setDirectConversationLoading] = React.useState(false);
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get('room');
  const navigate = useNavigate();

  // Handler to select a conversation and update the URL
  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    
    // Auto-switch to appropriate tab based on conversation type
    if (conv.orderId || conv.order_id) {
      setActiveTab('transactions');
    } else {
      setActiveTab('messages');
    }
    
    if (conv && conv.id) {
      navigate(`/messages?room=${conv.id}`, { replace: false });
    }
  };

  // Remove artificial setTimeout, set loading to false after Firestore fetch
  React.useEffect(() => {
    setSidebarLoading(true);
    // Loading state will be set to false after Firestore fetch below
  }, []);

  // Fetch transaction conversations from Firestore
  React.useEffect(() => {
    if (!user) return;
    setSidebarLoading(true);
    const q = query(
      collection(db, 'sellerbuyerconversations'),
      where('type', '==', 'transaction'),
      where('members', 'array-contains', user.email)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const txs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Add agent chat at the top (placeholder)
      const allTransactions = [
        {
          id: 'admin',
          name: 'Chat with Agent', // Placeholder name
          date: new Date().toLocaleDateString(),
          online: true,
          avatar: 'https://i.pravatar.cc/40?img=1', // Placeholder avatar
          lastMessage: 'How can I help you today?', // Placeholder message
          unreadCount: 0,
          isAdmin: true
        },
        ...txs.map(conv => ({
          ...conv,
          name: conv.seller?.name || conv.seller_name || 'Seller',
          avatar: conv.seller_avatar || conv.seller?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(conv.seller?.name || conv.seller_name || 'Seller')}`,
          date: conv.order_date || (conv.created_at && conv.created_at.toDate ? conv.created_at.toDate().toLocaleDateString() : ''),
          unreadCount: 0 // You can implement unread logic if needed
        }))
      ];
      setTransactions(allTransactions);
      setSidebarLoading(false); // Set loading to false after fetch
    }, (error) => {
      setSidebarLoading(false);
      // Log Firestore error
      // console.error('Error fetching transactions:', error);
    });
    return () => unsub();
  }, [user]);

  // Handle roomId changes in URL (for direct URL access)
  React.useEffect(() => {
    console.log('URL roomId:', roomId);
    console.log('Available transactions:', transactions.map(t => ({ id: t.id, name: t.name })));
    console.log('Current selectedConversation:', selectedConversation?.id);
    
    if (roomId && transactions.length > 0) {
      const targetConversation = transactions.find(conv => conv.id === roomId);
      console.log('Found target conversation:', targetConversation);
      if (targetConversation && (!selectedConversation || selectedConversation.id !== roomId)) {
        setSelectedConversation(targetConversation);
        
        // Auto-switch to appropriate tab based on conversation type
        if (targetConversation.orderId || targetConversation.order_id) {
          setActiveTab('transactions');
        } else {
          setActiveTab('messages');
        }
      }
    }
  }, [roomId, transactions]);

  // Handle direct conversation access when not found in user's list
  React.useEffect(() => {
    if (roomId && !selectedConversation && transactions.length > 0) {
      const targetConversation = transactions.find(conv => conv.id === roomId);
      if (!targetConversation && roomId !== 'admin') {
        // Try to fetch the conversation directly from Firestore
        setDirectConversationLoading(true);
        const fetchDirectConversation = async () => {
          try {
            console.log('Fetching direct conversation:', roomId);
            const docRef = doc(db, 'sellerbuyerconversations', roomId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              const convData = { id: docSnap.id, ...docSnap.data() };
              console.log('Found conversation data:', convData);
              // Check if user has access to this conversation
              if (convData.members && convData.members.includes(user.email)) {
                const formattedConversation = {
                  ...convData,
                  name: convData.seller?.name || convData.seller_name || 'Seller',
                  avatar: convData.seller_avatar || convData.seller?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(convData.seller?.name || convData.seller_name || 'Seller')}`,
                  date: convData.order_date || (convData.created_at && convData.created_at.toDate ? convData.created_at.toDate().toLocaleDateString() : ''),
                  unreadCount: 0
                };
                console.log('Setting selected conversation:', formattedConversation);
                setSelectedConversation(formattedConversation);
                
                // Auto-switch to appropriate tab based on conversation type
                if (formattedConversation.orderId || formattedConversation.order_id) {
                  setActiveTab('transactions');
                } else {
                  setActiveTab('messages');
                }
                
                // Add to transactions list if not already there
                setTransactions(prev => {
                  if (!prev.find(conv => conv.id === roomId)) {
                    return [prev[0], formattedConversation, ...prev.slice(1)];
                  }
                  return prev;
                });
              } else {
                console.log('User does not have access to this conversation');
                // Redirect to messages page without room parameter
                navigate('/messages');
              }
            } else {
              console.log('Conversation not found');
              // Redirect to messages page without room parameter
              navigate('/messages');
            }
                      } catch (error) {
              console.error('Error fetching direct conversation:', error);
              // Redirect to messages page without room parameter
              navigate('/messages');
            } finally {
              setDirectConversationLoading(false);
            }
          };
          fetchDirectConversation();
      }
    }
  }, [roomId, selectedConversation, transactions, user, navigate]);

  // Calculate total unread count
  const totalUnread = transactions.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);

  // Update agent chat preview in sidebar
  const updateAgentChatPreview = (text) => {
    setTransactions(prev => {
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        lastMessage: text,
        lastMessageTime: Date.now(),
        unreadCount: 0,
      };
      return updated;
    });
    setMessages(prev => {
      const updated = [...prev];
      updated[0] = {
        ...updated[0],
        lastMessage: text,
        lastMessageTime: Date.now(),
        unreadCount: 0,
      };
      return updated;
    });
  };

  const moveConversationToTop = (convId, newMessage) => {
    setTransactions(prev => {
      const idx = prev.findIndex(c => c.id === convId);
      if (idx === -1) return prev;
      const updated = [...prev];
      const now = Date.now();
      updated[idx] = {
        ...updated[idx],
        lastMessage: newMessage,
        lastMessageTime: now,
        unreadCount: (updated[idx].unreadCount || 0) + 1,
      };
      const [item] = updated.splice(idx, 1);
      updated.unshift(item);
      return updated;
    });
  };

  // Update any conversation's last message in messages state
  const updateMessagesPreview = (convId, newMessage) => {
    setMessages(prev => {
      const idx = prev.findIndex(c => c.id === convId);
      if (idx === -1) return prev;
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        lastMessage: newMessage,
        lastMessageTime: Date.now(),
        unreadCount: (updated[idx].unreadCount || 0) + 1,
      };
      // Move to top
      const [item] = updated.splice(idx, 1);
      updated.unshift(item);
      return updated;
    });
  };

  // --- Add Firestore listener for agent chat preview and unread count ---
  const agentUnsubRef = React.useRef();
  React.useEffect(() => {
    if (!user) return;
    const convId = `agent_${user.uid}`;
    const convRef = doc(db, 'agentConversations', convId);
    const messagesCol = collection(db, 'agentConversations', convId, 'messages');
    const q = query(messagesCol, orderBy('timestamp', 'asc'));
    let lastSeenByUser = null;
    getDoc(convRef).then(convSnap => {
      if (convSnap.exists()) {
        lastSeenByUser = convSnap.data().lastSeenByUser || null;
      }
      const unsub = onSnapshot(q, (snapshot) => {
        const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          const agentMsgs = msgs.filter(m => m.from === 'admin' || m.from === ADMIN_EMAIL || m.name === 'YTproud Agent');
          const lastSeenIndex = agentMsgs.findIndex(m => m.id === lastSeenByUser);
          const unreadCount = lastSeenIndex === -1 ? agentMsgs.length : agentMsgs.length - (lastSeenIndex + 1);
          setTransactions(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              lastMessage: lastMsg.text,
              lastMessageTime: Date.now(),
              unreadCount,
            };
            return updated;
          });
          setMessages(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              lastMessage: lastMsg.text,
              lastMessageTime: Date.now(),
              unreadCount,
            };
            return updated;
          });
        } else {
          setTransactions(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              lastMessage: 'How can I help you today?',
              lastMessageTime: Date.now(),
              unreadCount: 0,
            };
            return updated;
          });
          setMessages(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              lastMessage: 'How can I help you today?',
              lastMessageTime: Date.now(),
              unreadCount: 0,
            };
            return updated;
          });
        }
      });
      agentUnsubRef.current = unsub;
    }).catch((error) => {
      // console.error('Error fetching agent conversation:', error);
    });
    return () => {
      if (agentUnsubRef.current) agentUnsubRef.current();
    };
  }, [user, selectedConversation]);

  // Reset unread count when agent chat is selected/read and update Firestore
  React.useEffect(() => {
    if (selectedConversation && selectedConversation.id === 'admin') {
      // Mark all as read
      const convId = `agent_${user?.uid}`;
      const convRef = doc(db, 'agentConversations', convId);
      const messagesCol = collection(db, 'agentConversations', convId, 'messages');
      const q = query(messagesCol, orderBy('timestamp', 'asc'));
      getDocs(q).then(snapshot => {
        const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Find the last agent message
        const agentMsgs = msgs.filter(m => m.from === 'admin' || m.from === ADMIN_EMAIL || m.name === 'YTproud Agent');
        if (agentMsgs.length > 0) {
          const lastAgentMsg = agentMsgs[agentMsgs.length - 1];
          // Store in Firestore
          updateDoc(convRef, { lastSeenByUser: lastAgentMsg.id });
          setTransactions(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              unreadCount: 0,
            };
            return updated;
          });
          setMessages(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              unreadCount: 0,
            };
            return updated;
          });
        }
      });
    }
  }, [selectedConversation, user]);

  return (
    <div className="w-full h-screen bg-gray-100">
      <div className="flex h-full w-full bg-gray-100">
        {/* Sidebar - Hidden on mobile/tablet when conversation selected, visible on desktop */}
        <ErrorBoundary>
          <React.Suspense fallback={<div style={{padding: 40, textAlign: 'center'}}>Loading...</div>}>
            <div className={`${selectedConversation ? 'hidden' : 'w-full'} xl:block`}>
              <Sidebar
                transactions={transactions}
                messages={messages}
                moveConversationToTop={moveConversationToTop}
                onSelectConversation={handleSelectConversation}
                selectedConversationId={selectedConversation?.id}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                totalUnread={totalUnread}
                sidebarWidth={380}
                sidebarLoading={sidebarLoading}
              />
            </div>
          </React.Suspense>
        </ErrorBoundary>
        {/* Chat Panel - Full width on mobile/tablet when conversation selected */}
        <ErrorBoundary>
          <React.Suspense fallback={<div style={{padding: 40, textAlign: 'center'}}>Loading...</div>}>
            <div className={`${selectedConversation ? 'flex' : 'hidden'} xl:flex flex-1`}>
              <ChatPanel
                conversation={selectedConversation}
                activeTab={activeTab}
                moveConversationToTop={moveConversationToTop}
                onSelectConversation={handleSelectConversation}
                transactions={transactions}
                agentMessages={agentMessages}
                setAgentMessages={setAgentMessages}
                updateAgentChatPreview={updateAgentChatPreview}
                updateMessagesPreview={updateMessagesPreview}
                messages={messages}
                setMessages={setMessages}
                selectedConversationId={selectedConversation?.id}
              />
            </div>
          </React.Suspense>
        </ErrorBoundary>
        {/* Product Detail - Hidden on mobile/tablet, visible on desktop */}
        {selectedConversation && selectedConversation.id !== 'admin' && (
          <div className="hidden xl:block">
            <ProductDetail conversation={selectedConversation} isMobile={false} />
          </div>
        )}
      </div>
    </div>
  );
}

// ScrollToTop component
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const { initializeAuth } = useAuthStore();

  // Initialize authentication on app start
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <HelmetProvider>
    <DarkModeProvider>
    <Router>
      <ScrollToTop />
      <UseSetOnlineStatusGlobal />
      <GoogleAnalytics />
      {/* Basic viewport and theme meta tags - SEO controlled via admin dashboard */}
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#3B82F6" />
      </Helmet>
      <React.Suspense fallback={<div style={{padding: 40, textAlign: 'center'}}>Loading...</div>}>
      <Routes>
        <Route path="/login" element={<Auth />} />
        <Route path="/signup" element={<Auth />} />
        <Route path="/" element={<Home />} />
        <Route
          path="/messages/admin"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <ChatApp />
            </ProtectedRoute>
          }
        />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/buy-youtube-channel" element={<BuyYouTubeChannel />} />
        <Route path="/buy-tiktok-account" element={<BuyTiktokAccount />} />
        <Route path="/buy-facebook-page" element={<BuyFacebookPage />} />
        <Route path="/buy-instagram-account" element={<BuyInstagramAccount />} />
        <Route path="/product/:slug" element={<ProductView />} />
        <Route 
          path="/sell-account" 
          element={
            <ProtectedRoute>
              <>
                <Header />
                <SellAccount />
                <Footer />
              </>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/about" 
          element={
            <>
              <Header />
              <AboutUs />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/contact" 
          element={
            <>
              <Header />
              <ContactUs />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/blog" 
          element={
            <>
              <Header />
              <Blog />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/blog/:slug" 
          element={
            <>
              <Header />
              <BlogDetail />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/delivery-faq" 
          element={
            <>
              <Header />
              <DeliveryFAQ />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/terms" 
          element={
            <>
              <Header />
              <TermsAndConditions />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/refund-policy" 
          element={
            <>
              <Header />
              <RefundAndReturnPolicy />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/privacy-policy" 
          element={
            <>
              <Header />
              <PrivacyPolicy />
              <Footer />
            </>
          } 
        />
        <Route 
          path="/checkout" 
          element={
            <ProtectedRoute>
              <>
                <Header />
                <Checkout />
                <Footer />
              </>
            </ProtectedRoute>
          } 
        />
        <Route path="/webadmin" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminDashboardSite />
          </ProtectedRoute>
        } />
        <Route path="/adminlogin" element={<AdminLogin />} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/profile/:username" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/wise-payment-details" element={<WisePaymentDetails />} />
        <Route 
          path="/wishlist" 
          element={
            <ProtectedRoute>
              <>
                <Header />
                <Wishlist />
                <Footer />
              </>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/cart" 
          element={
            <ProtectedRoute>
              <>
                <Header />
                <Cart />
                <Footer />
              </>
            </ProtectedRoute>
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </React.Suspense>
      <ToastContainer
        position="top-center"
        autoClose={4000}
        toastClassName="!w-[420px] !min-h-[70px] !rounded-2xl !shadow-2xl !bg-white !text-gray-900 !border !border-blue-200 !flex !items-center"
        bodyClassName="!text-lg !font-semibold !flex !items-center"
        icon={<FaExclamationCircle className="text-red-500 text-3xl mr-3" />}
        closeButton={false}
        hideProgressBar={false}
        draggable={false}
        pauseOnHover
      />
      <MobileFooterBar />
      <CacheManager />
    </Router>
    </DarkModeProvider>
    </HelmetProvider>
  );
}

export default App;
